/* eslint-disable @next/next/no-img-element */
"use client";

import { useEffect } from "react";

import type { IInternshipCourse } from "@/api/internshipData";
import type {
  IInternshipEnrolmentResponse,
  IInternshipTransaction,
} from "@/api/internshipPayment";
import { useAtomPayment } from "@/hooks/useAtomPayment";

import PaymentStatus from "./PaymentStatus";
import {
  PRIMARY_BUTTON_CLASSNAME,
  SECONDARY_BUTTON_CLASSNAME,
} from "./primitives";
import Spinner from "../Spinner";

/**
 * Step 3 of the Apply Now flow — ported from the source's `PaymentInfoForm`.
 *
 * Two different screens live here, exactly as in the source:
 *  - No transaction in hand → the order summary and a Pay Now button.
 *  - A transaction (i.e. the student came back from Atom via `?transactionId=`)
 *    → the outcome screen, and the summary is not rendered at all.
 *
 * Pay Now enrols first; the enrolment response carries the Atom grant, and the
 * effect below hands it to the checkout SDK, which navigates away.
 */

interface PaymentInfoFormProps {
  onBack: () => void;
  onPaymentSubmit: () => void;
  course?: IInternshipCourse;
  transactionDetails?: IInternshipTransaction;
  enrollmentError: string;
  isFetchingTransactionDetails: boolean;
  isEnrollmentLoader: boolean;
  enrollmentResponse?: IInternshipEnrolmentResponse | null;
}

export default function PaymentInfoForm({
  onBack,
  onPaymentSubmit,
  course,
  transactionDetails,
  enrollmentError,
  isFetchingTransactionDetails,
  isEnrollmentLoader,
  enrollmentResponse,
}: PaymentInfoFormProps) {
  const {
    initiateAtomPayment,
    isLoading: atomLoading,
    error: atomError,
  } = useAtomPayment();

  const courseTitle = course?.course_name || "";
  const feeAmount = Number(course?.fee_amount) || 0;
  const imageUrl = course?.image_url || "";
  const paymentStatus = transactionDetails?.paymentStatus || "";

  // Open Atom as soon as a grant arrives. Every field is required by the SDK,
  // so a partial response is left alone rather than half-initialising checkout.
  useEffect(() => {
    if (!enrollmentResponse || enrollmentError) return;

    const { token, merchantID, returnUrl, redirectionUrl, email, mobileNumber } =
      enrollmentResponse;

    if (token && merchantID && returnUrl && email && mobileNumber) {
      initiateAtomPayment(
        { token, merchantID, returnUrl, redirectionUrl },
        email,
        mobileNumber
      );
    }
    // `initiateAtomPayment` is stable; re-running on it would reopen checkout.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [enrollmentResponse, enrollmentError]);

  if (isFetchingTransactionDetails) {
    return (
      <div className="flex justify-center items-center flex-col">
        <Spinner size="w-20 h-20" color="text-[#9F1C33]" />
        <p className="my-5">
          Hang in there we are fetching your payment details
        </p>
      </div>
    );
  }

  if (transactionDetails) {
    return <PaymentStatus status={paymentStatus} />;
  }

  const isBusy = isEnrollmentLoader || atomLoading;

  return (
    <div className="space-y-6">
      <div className="space-y-6">
        <div className="flex flex-col tab:flex-row items-start gap-4">
          <div className="w-full tab:w-1/3">
            {imageUrl ? (
              <img
                src={imageUrl}
                alt={courseTitle}
                /* Source has `h-30`, which Tailwind v3 does not generate — the
                   image renders at its natural height there. v4 WOULD generate
                   it (7.5rem) and crop, so it is dropped to keep parity. */
                className="rounded-md w-full object-cover"
              />
            ) : null}
          </div>
          <div className="w-full tab:w-2/3">
            <div className="inline-block px-3 py-1 bg-[#EFF8FF] text-[#2165AA] rounded-full text-xs tab:text-sm mb-2">
              Internship Programme
            </div>
            <h3 className="text-sm tab:text-lg font-medium">{courseTitle}</h3>
          </div>
        </div>
      </div>

      <div className="bg-[#FCE7F6] rounded-lg p-6 mt-6">
        <div className="flex justify-between items-center flex-col tab:flex-row">
          <div className="text-lg font-medium">Total Fee :</div>
          <div className="text-2xl font-bold mt-2 tab:mt-0">
            ₹ {feeAmount.toLocaleString("en-IN")}/-
          </div>
        </div>
      </div>

      <p className="text-[#E53935] text-center">
        {enrollmentError || atomError}
      </p>

      <div className="flex justify-center gap-5 mt-8">
        <button
          type="button"
          onClick={onBack}
          disabled={isBusy}
          className={`${SECONDARY_BUTTON_CLASSNAME} text-lg`}
        >
          Back
        </button>
        <button
          type="button"
          onClick={onPaymentSubmit}
          disabled={isBusy}
          className={`${PRIMARY_BUTTON_CLASSNAME} text-lg`}
        >
          {isBusy ? <Spinner color="text-[#FFFFFF]" /> : "Pay Now"}
        </button>
      </div>
    </div>
  );
}
